import { Button, EmptyState } from "../../design-v2";
import EditModeToolbar from "./EditModeToolbar";

// Weekly Builder workout-list presentation only. The container remains the
// authority for selection, validation, action gates, navigation and every
// structural mutation.
export default function WorkoutListEditable({
  workouts,
  workoutNameValidationById,
  selectedWorkoutIds,
  selectedWorkoutIdSet,
  isEditMode,
  canMoveUp,
  canMoveDown,
  canDuplicate,
  canDelete,
  canCreateWorkout,
  nextWorkoutNumber,
  onToggleEditMode,
  onToggleWorkoutSelection,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onOpenWorkout,
  onAddWorkout,
}) {
  const hasSelection = selectedWorkoutIds.length > 0;

  return (
    <section className="lz-builder-workout-list" aria-labelledby="weekly-workout-list-title">
      <div className="lz-builder-schedule__header">
        <h2 className="lz-builder-schedule__title" id="weekly-workout-list-title">
          Workout Schedule
        </h2>
        <button
          type="button"
          onClick={onToggleEditMode}
          className="lz-builder-schedule__edit"
          aria-pressed={isEditMode}
        >
          Edit
        </button>
      </div>

      <div
        className="lz-builder-collapse lz-builder-collapse--toolbar"
        data-expanded={isEditMode && hasSelection ? "true" : "false"}
        aria-hidden={isEditMode && hasSelection ? undefined : "true"}
        inert={isEditMode && hasSelection ? undefined : ""}
      >
        <div className="lz-builder-collapse__inner">
          <EditModeToolbar
            label={`${selectedWorkoutIds.length} selected`}
            onMovePrevious={onMoveUp}
            movePreviousDisabled={!canMoveUp}
            onMoveNext={onMoveDown}
            moveNextDisabled={!canMoveDown}
            onDuplicate={onDuplicate}
            duplicateDisabled={!canDuplicate}
            onDelete={onDelete}
            deleteDisabled={!canDelete}
            movePreviousLabel="Move selected workouts up"
            movePreviousTitle="Move up"
            moveNextLabel="Move selected workouts down"
            moveNextTitle="Move down"
            duplicateLabel="Duplicate selected workouts"
            duplicateTitle="Duplicate"
            deleteLabel="Delete selected workouts"
            deleteTitle="Delete"
          />
        </div>
      </div>

      {workouts.length > 0 ? (
        <div className="lz-builder-workout-list__rows">
          {workouts.map((workout, index) => {
            const validation = workoutNameValidationById[workout.id] || {
              nameError: "",
            };
            const isSelected = selectedWorkoutIdSet.has(workout.id);
            const isEmptyWorkout =
              !Array.isArray(workout.blocks) || workout.blocks.length === 0;
            const isInvalid = Boolean(validation.nameError || isEmptyWorkout);

            return (
              <button
                key={workout.id}
                type="button"
                onClick={() =>
                  isEditMode
                    ? onToggleWorkoutSelection(workout.id)
                    : onOpenWorkout(workout.id)
                }
                className={[
                  "lz-builder-workout-row",
                  isSelected ? "lz-builder-workout-row--selected" : "",
                  isInvalid ? "lz-builder-workout-row--invalid" : "",
                ].filter(Boolean).join(" ")}
                aria-pressed={isEditMode ? isSelected : undefined}
              >
                <span className="lz-builder-workout-row__leading" aria-hidden="true">
                  {isEditMode ? (
                    <span className="lz-builder-workout-row__checkbox">
                      {isSelected ? "✓" : ""}
                    </span>
                  ) : (
                    <span className="lz-builder-workout-row__index">{index + 1}</span>
                  )}
                </span>

                <span className="lz-builder-workout-row__copy">
                  <span className="lz-builder-workout-row__name">
                    {workout.name}
                  </span>
                  {validation.nameError ? (
                    <span className="lz-builder-workout-row__error" role="alert">
                      {validation.nameError}
                    </span>
                  ) : null}
                  {isEmptyWorkout ? (
                    <span className="lz-builder-workout-row__error">Add at least 1 block</span>
                  ) : null}
                  <span className="lz-builder-workout-row__meta">{workout.meta}</span>
                </span>

                {!isEditMode ? (
                  <svg className="lz-builder-workout-row__chevron" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="m6 3.5 4.5 4.5L6 12.5" />
                  </svg>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : (
        <EmptyState
          className="lz-builder-workout-list__empty"
          title="No workouts yet"
          body="Create your first workout to start building this weekly template."
          action={
            canCreateWorkout ? (
              <Button onClick={() => onAddWorkout()}>Create Workout {nextWorkoutNumber}</Button>
            ) : null
          }
        />
      )}

      {workouts.length > 0 && canCreateWorkout ? (
        <button
          type="button"
          onClick={() => onAddWorkout()}
          className="lz-builder-workout-list__add"
        >
          <span aria-hidden="true">+</span>
          <span>Create Workout {nextWorkoutNumber}</span>
        </button>
      ) : null}
    </section>
  );
}
